<script>
export default {
  name: 'PlanSelection',
  emits: ['select-plan'],
  props: {
    pricing: {
      type: Object,
      default: () => ({}),
    },
    currentPlanId: {
      type: String,
      default: null,
    },
    showHeader: {
      type: Boolean,
      default: true,
    },
    actionLabel: {
      type: String,
      default: null,
    },
    disableCurrentPlan: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    normalizedCurrentPlanId() {
      return this.currentPlanId
        ? this.currentPlanId.toString().trim().toLowerCase()
        : null;
    },
    fixedPrices() {
      const fixed = this.pricing?.fixed || {};
      return {
        basic: Number.isFinite(Number(fixed?.basic?.amount)) ? Number(fixed.basic.amount) : null,
        pro: Number.isFinite(Number(fixed?.pro?.amount)) ? Number(fixed.pro.amount) : null,
        max: Number.isFinite(Number(fixed?.max?.amount)) ? Number(fixed.max.amount) : null,
      };
    },
    modularBasePrice() {
      const amount = Number(this.pricing?.modular?.base?.amount);
      return Number.isFinite(amount) ? amount : null;
    },
    plans() {
      return [
        {
          id: 'modular',
          name: this.$t('PLANS.MODULAR.NAME'),
          price: this.modularBasePrice,
          price_prefix: this.$t('PLANS.MODULAR.PREFIX'),
          trial_days: parseInt(window.chatwootConfig?.PLAN_TRIAL_DAYS_MODULAR) || 0,
          features: [
            this.$t('PLANS.MODULAR.FEATURES.1'),
            this.$t('PLANS.MODULAR.FEATURES.2'),
            this.$t('PLANS.MODULAR.FEATURES.3'),
            this.$t('PLANS.MODULAR.FEATURES.4')
          ],
        },
        {
          id: 'basic',
          name: this.$t('PLANS.BASIC.NAME'),
          price: this.fixedPrices.basic,
          trial_days: parseInt(window.chatwootConfig?.PLAN_TRIAL_DAYS_BASIC) || 0,
          features: [
            this.$t('PLANS.BASIC.FEATURES.1'),
            this.$t('PLANS.BASIC.FEATURES.2'),
            this.$t('PLANS.BASIC.FEATURES.3'),
            this.$t('PLANS.BASIC.FEATURES.4'),
            this.$t('PLANS.BASIC.FEATURES.5')
          ]
        },
        {
          id: 'pro',
          name: this.$t('PLANS.PRO.NAME'),
          price: this.fixedPrices.pro,
          trial_days: parseInt(window.chatwootConfig?.PLAN_TRIAL_DAYS_PRO) || 0,
          features: [
            this.$t('PLANS.PRO.FEATURES.1'),
            this.$t('PLANS.PRO.FEATURES.2'),
            this.$t('PLANS.PRO.FEATURES.3'),
            this.$t('PLANS.PRO.FEATURES.4'),
            this.$t('PLANS.PRO.FEATURES.5')
          ]
        },
        {
          id: 'max',
          name: this.$t('PLANS.MAX.NAME'),
          price: this.fixedPrices.max,
          trial_days: parseInt(window.chatwootConfig?.PLAN_TRIAL_DAYS_MAX) || 0,
          features: [
            this.$t('PLANS.MAX.FEATURES.1'),
            this.$t('PLANS.MAX.FEATURES.2'),
            this.$t('PLANS.MAX.FEATURES.3'),
            this.$t('PLANS.MAX.FEATURES.4'),
            this.$t('PLANS.MAX.FEATURES.5')
          ]
        }
      ];
    },
  },
  methods: {
    isCurrentPlan(plan) {
      return (
        this.normalizedCurrentPlanId &&
        plan?.id === this.normalizedCurrentPlanId
      );
    },
    selectPlan(plan) {
      if (!this.isPlanSelectable(plan)) return;
      this.$emit('select-plan', plan);
    },
    formatPrice(price) {
      if (!Number.isFinite(price)) {
        return this.$t('PLANS.PRICE_UNAVAILABLE');
      }
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(price);
    },
    isPlanPriceAvailable(plan) {
      return Number.isFinite(plan?.price);
    },
    isPlanSelectable(plan) {
      if (!this.isPlanPriceAvailable(plan)) return false;
      if (this.disableCurrentPlan && this.isCurrentPlan(plan)) return false;
      return true;
    },
    actionLabelFor(plan) {
      if (this.isCurrentPlan(plan) && this.disableCurrentPlan) {
        return this.$t('REGISTER.PLANS.SELECTED');
      }
      if (this.actionLabel) {
        return this.actionLabel;
      }
      return this.$t('REGISTER.PLANS.SUBSCRIBE_BUTTON');
    },
  },
};
</script>

<template>
  <div class="plan-selection max-w-[1304px] mx-auto">
    <div v-if="showHeader">
      <h2 class="text-2xl font-semibold text-slate-900 dark:text-woot-50 mb-2">
        {{ $t('REGISTER.PLANS.TITLE') }}
      </h2>
      <p class="text-slate-600 dark:text-slate-400 mb-8">
        {{ $t('REGISTER.PLANS.SUBTITLE') }}
      </p>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-6">
      <div
      v-for="plan in plans"
      :key="plan.id"
      class="plan-card relative flex flex-col p-6 bg-white dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-700 rounded-xl hover:border-responza-500 dark:hover:border-responza-400 hover:shadow-lg transition-all cursor-pointer"
      :class="{
        'opacity-60 cursor-not-allowed hover:shadow-none': !isPlanSelectable(plan),
        'border-responza-500 ring-2 ring-responza-500': isCurrentPlan(plan),
      }"
      @click="selectPlan(plan)"
      >
      
        <div class="flex-1">
          <div class="flex items-center gap-2 mb-2">
            <h3 class="text-xl font-semibold text-slate-900 dark:text-woot-50">
              {{ plan.name }}
            </h3>
            <span
              v-if="isCurrentPlan(plan)"
              class="text-xs font-semibold uppercase tracking-wide px-2 py-1 rounded-full bg-responza-100 text-responza-700"
            >
              {{ $t('REGISTER.PLANS.SELECTED') }}
            </span>
          </div>

          <div class="mb-4">
            <span
              v-if="plan.price_prefix"
              class="block text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400"
            >
              {{ plan.price_prefix }}
            </span>
            <span class="text-3xl font-bold text-responza-500 dark:text-responza-400">
              {{ formatPrice(plan.price) }}
            </span>
            <span
              v-if="isPlanPriceAvailable(plan)"
              class="text-slate-500 dark:text-slate-400"
            >
              {{ $t('PLANS.PER_MONTH') }}
            </span>
          </div>

          <ul class="space-y-2 mb-6">
            <li
              v-for="(feature, index) in plan.features"
              :key="index"
              class="flex items-center text-sm text-slate-600 dark:text-slate-300"
            >
              <svg
                class="w-4 h-4 mr-2 text-responza-500 dark:text-responza-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
              {{ feature }}
            </li>
          </ul>
        </div>

        <button
          type="button"
          class="w-full py-3 px-4 bg-responza-500 hover:bg-responza-600 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="!isPlanSelectable(plan)"
        >
          {{ actionLabelFor(plan) }}
          <span
            v-if="plan.trial_days > 0 && !isCurrentPlan(plan) && showHeader"
            class="block text-sm font-normal opacity-90"
          >
            ({{ plan.trial_days }} {{ $t('REGISTER.PLANS.TRIAL_DAYS') }})
          </span>
        </button>
      </div>
    </div>

    <!-- Link Mais Informações -->
    <div class="text-center mt-6">
      <a
        href="https://responza.com.br/#pricing"
        target="_blank"
        rel="noopener noreferrer"
        class="text-sm font-medium text-responza-500 dark:text-responza-400 hover:text-responza-600 dark:hover:text-responza-300 transition-colors"
        style="text-decoration: underline; text-underline-offset: 4px;"
      >
        {{ $t('PLANS.MORE_INFO') }}
      </a>
    </div>
  </div>
</template>

<style scoped>
.plan-card:hover {
  transform: translateY(-2px);
}
</style>
